import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from 'luxon';
import './wallet.css';

// ---------------------------------------------------------------------------
// Staff visibility for GATEWAY REVIEW-HELD card checkouts (2026-07-18:
// "I have not even any idea what to do with these pending payments").
//
// A review hold is NOT a queue we can action through the API — N-Genius owns
// the decision and there is no approve/decline call. So this panel is
// deliberately READ-ONLY: its whole job is (a) make the holds visible at all,
// (b) say in plain English what they are and what happens next, and (c) point
// staff at the one place the hold CAN be resolved (the N-Genius portal).
//
// Everything money-related here is descriptive of state the server already
// decided. This screen never claims a payment did or did not settle — it
// reports the gateway state the backend last observed, and the automatic
// release that the 60-minute timeout rung will perform.
// ---------------------------------------------------------------------------

// The one-hour rung in routes/payments.js (REVIEW_TIMEOUT_MINUTES). Kept as a
// named constant so the copy and the "overdue" styling can never disagree.
export const REVIEW_TIMEOUT_MINUTES = 60;

export const REVIEW_HELD_GUIDANCE =
    'This card payment was authorised but the gateway is holding it for fraud review. '
    + `Resolve it in the N-Genius portal; if it is still unresolved after ${REVIEW_TIMEOUT_MINUTES} minutes `
    + 'we void it automatically and cancel the request.';

// Tolerant list reader — the endpoint may answer a bare array or a wrapped
// object, and a server that predates it 404s (handled by the caller).
// `reviews` is the key the live endpoint (GET /api/payments/admin/gateway-reviews)
// actually returns; the rest are kept so an older or reshaped payload still reads.
export const readHeldRows = (data) => {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return [];
    return data.reviews || data.checkouts || data.held || data.orders || data.rows || [];
};

// The endpoint answers 200 with an EMPTY list when the 2026_07_payments_11 review
// columns are missing (schema_ready:false). That is not "no holds" — it is "we
// cannot tell", and it must render like the 404 branch, never as an all-clear.
export const readSchemaReady = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return true;
    return data.schema_ready !== false;
};

// Field readers. Every one degrades to null rather than rendering "undefined",
// because this panel's value is trust: a wrong-looking row is worse than a dash.
const orderRef = (r) => r.gateway_order_ref || r.order_ref || r.reference || r.ref || null;
const clientName = (r) => r.client_name || r.display_name || (r.client_id ? `Client #${r.client_id}` : '—');
const requestId = (r) => r.client_request_id ?? r.request_id ?? null;
// The hold clock is review_first_seen_at (2026_07_payments_11) — the FIRST
// money-state observation, not created_at. created_at includes however long the
// client sat on the paypage before paying, which would overstate every hold.
const heldSince = (r) => r.review_first_seen_at || r.review_notified_at || null;

// Minutes held, or null when we have no clock (pre-migration rows).
export const heldMinutes = (r, now = DateTime.now()) => {
    const since = heldSince(r);
    if (!since) return null;
    const dt = DateTime.fromISO(since);
    if (!dt.isValid) return null;
    return Math.max(0, Math.floor(now.diff(dt, 'minutes').minutes));
};

export const heldForLabel = (r, now = DateTime.now()) => {
    const mins = heldMinutes(r, now);
    if (mins == null) return 'Unknown';
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem === 0 ? `${hours} h` : `${hours} h ${rem} min`;
};

export default function ReviewHeldPaymentsSection({ onCountChange }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // `unavailable` = the endpoint isn't deployed on this server. Distinct from
    // "no holds": staff must not read a missing endpoint as an all-clear.
    const [unavailable, setUnavailable] = useState(false);

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (d) => (d ? DateTime.fromISO(d, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy, HH:mm') : '—');

    const fetchHeld = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get('/api/payments/admin/gateway-reviews');
            const ready = readSchemaReady(res.data);
            const list = ready ? readHeldRows(res.data) : [];
            setRows(list);
            // A server missing the review columns can't answer the question, so
            // it takes the same "we cannot tell you" branch as a 404.
            setUnavailable(!ready);
            if (onCountChange) onCountChange(list.length);
        } catch (err) {
            if (err.response?.status === 404) {
                setUnavailable(true);
            } else {
                console.error('Error fetching review-held payments:', err);
            }
            setRows([]);
            if (onCountChange) onCountChange(0);
        } finally {
            setLoading(false);
        }
    }, [onCountChange]);

    useEffect(() => {
        fetchHeld();
        // A hold resolves itself (gateway decision or our 60-minute rung), so a
        // page opened and left on screen must not show a frozen list.
        const t = setInterval(fetchHeld, 60000);
        return () => clearInterval(t);
    }, [fetchHeld]);

    // No holds: render nothing (owner, 2026-09-07) — the overview row is where
    // "nothing held" is said. A server that CANNOT answer still shows its
    // warning below; that is not an all-clear.
    if (!loading && !unavailable && rows.length === 0) return null;

    return (
        <div className="swallet__section">
            <div className="swallet__sectionHead">
                <h2 className="swallet__sectionTitle">
                    Payments Held for Review
                    {rows.length > 0 && (
                        <span className="swallet__sectionBadge">{rows.length}</span>
                    )}
                </h2>
                <span className="swallet__sectionSub">
                    Read-only — the gateway owns this decision, not the portal.
                </span>
            </div>

            <div className="swallet__guidance">
                <strong>What these are:</strong> {REVIEW_HELD_GUIDANCE}
                <div className="swallet__guidanceList">
                    <div>• The client has already been told, and should not pay again.</div>
                    <div>
                        • <strong>Do nothing here.</strong> If you can clear it, do it in the N-Genius
                        portal — the portal has no approve/decline API for a held order.
                    </div>
                    <div>
                        • On automatic release we <strong>void</strong> an authorisation, or{' '}
                        <strong>refund</strong> it if the gateway already captured, then cancel the
                        linked request and notify the client.
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="swallet__empty">Loading...</div>
            ) : unavailable ? (
                <div className="swallet__empty">
                    Held-payment visibility isn&apos;t available on this server yet — it needs the
                    latest deploy and database migration. This is <strong>not</strong> a
                    confirmation that there are none.
                </div>
            ) : rows.length === 0 ? (
                <div className="swallet__empty">No payments are being held for review.</div>
            ) : (
                <table className="swallet__table">
                    <thead>
                        <tr>
                            <th>Order Ref</th>
                            <th>Client</th>
                            <th>Amount</th>
                            <th>Held For</th>
                            <th>Held Since</th>
                            <th>Gateway State</th>
                            <th>Request</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const mins = heldMinutes(r);
                            const overdue = mins != null && mins >= REVIEW_TIMEOUT_MINUTES;
                            const rid = requestId(r);
                            return (
                                <tr key={orderRef(r) || r.id}>
                                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                        {orderRef(r) || '—'}
                                    </td>
                                    <td style={{ fontWeight: 900 }}>{clientName(r)}</td>
                                    <td style={{ fontWeight: 900 }}>{money(r.amount)}</td>
                                    <td>
                                        <span className={`swallet__chip ${overdue ? 'swallet__chip--red' : 'swallet__chip--orange'}`}>
                                            {heldForLabel(r)}
                                        </span>
                                        {overdue && (
                                            <div className="swallet__cellNote">
                                                Past {REVIEW_TIMEOUT_MINUTES} min — automatic release is due.
                                            </div>
                                        )}
                                    </td>
                                    <td>{formatDate(heldSince(r))}</td>
                                    <td>
                                        <span className="swallet__chip swallet__chip--blue">
                                            {r.gateway_state || 'UNDER REVIEW'}
                                        </span>
                                    </td>
                                    <td>
                                        {rid ? (
                                            <Link to={`/requests?request=${rid}`} className="swallet__proofLink">
                                                Request #{rid}
                                            </Link>
                                        ) : r.booking_id ? (
                                            <Link to={`/bookings/${r.booking_id}`} className="swallet__proofLink">
                                                Booking #{r.booking_id}
                                            </Link>
                                        ) : (
                                            <span style={{ color: '#999' }}>Wallet top-up</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
